import type { CartItem } from '../redux/cartSlice';
import type { ProductObject, ProductVariant } from '../redux/productSlice';
import { isNumericEffectivelyZero, NUMERIC_DECIMAL_PLACES } from './libs';
import {
  composeProductVariantLabel,
  getProductDisplayName,
} from './productDisplayName';

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getVariants = (product: ProductObject | null | undefined): ProductVariant[] =>
  Array.isArray(product?.bp_product_variants)
    ? (product?.bp_product_variants as ProductVariant[])
    : [];

export const isVariantProduct = (
  product: ProductObject | null | undefined,
): boolean => Boolean(product?.bp_product_has_variants);

export const getTotalVariantStock = (
  product: ProductObject | null | undefined,
): number => getVariants(product).reduce((sum, variant) => sum + toNumber(variant.bp_variant_quantity), 0);

export const getEffectiveQuantity = (
  product: ProductObject | null | undefined,
  variantId?: string | null,
): number => {
  if (!product) return 0;
  if (!isVariantProduct(product)) return toNumber(product.bp_product_quantity);

  if (variantId) {
    const selected = getVariants(product).find(variant => variant._id === variantId);
    return selected ? toNumber(selected.bp_variant_quantity) : 0;
  }

  return getTotalVariantStock(product);
};

export const getEffectiveSellingPrice = (
  product: ProductObject | null | undefined,
  variantId?: string | null,
): number | null => {
  if (!product) return null;
  if (!isVariantProduct(product)) return product.bp_product_selling_price ?? null;

  const variants = getVariants(product);
  if (variantId) {
    const selected = variants.find(variant => variant._id === variantId);
    return selected ? toNumber(selected.bp_variant_selling_price) : null;
  }

  if (!variants.length) return null;
  const prices = variants.map(variant => toNumber(variant.bp_variant_selling_price));
  return Math.min(...prices);
};

export const getEffectiveBuyingPrice = (
  product: ProductObject | null | undefined,
  variantId?: string | null,
): number | null => {
  if (!product) return null;
  if (!isVariantProduct(product)) return product.bp_product_buying_price ?? null;

  const variants = getVariants(product);
  if (variantId) {
    const selected = variants.find(variant => variant._id === variantId);
    return selected ? toNumber(selected.bp_variant_buying_price) : null;
  }

  if (!variants.length) return null;
  const prices = variants.map(variant => toNumber(variant.bp_variant_buying_price));
  return Math.min(...prices);
};

export const getEffectiveBarcode = (
  product: ProductObject | null | undefined,
  variantId?: string | null,
): string => {
  if (!product) return '';
  if (!isVariantProduct(product)) return String(product.bp_product_barcode ?? '');

  const variants = getVariants(product);
  if (variantId) {
    const selected = variants.find(variant => variant._id === variantId);
    return String(selected?.bp_variant_barcode ?? '');
  }

  return '';
};

const formatCurrency = (amount: number): string =>
  toNumber(amount).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: NUMERIC_DECIMAL_PLACES,
  });

export const getPriceRangeDisplay = (
  product: ProductObject | null | undefined,
  currencySymbol: string = '',
): string => {
  if (!product) return `${currencySymbol}0`;

  if (!isVariantProduct(product)) {
    return `${currencySymbol}${formatCurrency(toNumber(product.bp_product_selling_price))}`;
  }

  const variants = getVariants(product);
  if (!variants.length) return `${currencySymbol}0`;

  const prices = variants.map(variant => toNumber(variant.bp_variant_selling_price));
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  if (minPrice === maxPrice) return `${currencySymbol}${formatCurrency(minPrice)}`;
  return `${currencySymbol}${formatCurrency(minPrice)} - ${currencySymbol}${formatCurrency(maxPrice)}`;
};

export const findVariantByBarcode = (
  product: ProductObject | null | undefined,
  barcode: string,
): ProductVariant | null => {
  if (!product || !barcode) return null;
  const trimmed = barcode.trim();
  if (!trimmed) return null;
  return (
    getVariants(product).find(
      variant => String(variant.bp_variant_barcode || '').trim() === trimmed,
    ) ?? null
  );
};

export const getVariantDisplayName = (
  product: ProductObject | null | undefined,
  variant: ProductVariant | null | undefined,
): string => {
  // No variant selected → full catalog label (name + mass/size/model).
  if (!variant) return getProductDisplayName(product);

  // display_name may already be full ("Shirt - Red") or attrs-only ("Red").
  const variantLabel =
    String(variant.bp_variant_display_name || '').trim() ||
    Object.values(variant.bp_variant_attributes || {})
      .filter(Boolean)
      .join(', ');
  return composeProductVariantLabel(product?.bp_product_name, variantLabel);
};

export const buildCartItemFromVariant = (
  product: ProductObject,
  variant: ProductVariant,
  existingCartItem?: CartItem,
): CartItem => ({
  ...(existingCartItem ?? {}),
  productID: product._id,
  variantId: variant._id,
  quantity: toNumber(variant.bp_variant_quantity),
  category: product.bp_product_category,
  itemname: getVariantDisplayName(product, variant),
  mass: toNumber(product.bp_product_mass),
  unitofmass: product.bp_product_unit_of_mass,
  model: product.bp_product_model,
  size: product.bp_product_size,
  image: variant.bp_variant_image || product.bp_product_image,
  price: toNumber(variant.bp_variant_selling_price),
  price_type: product.bp_product_price_type,
});

export const hasLowStockVariant = (
  product: ProductObject | null | undefined,
): boolean => {
  if (!isVariantProduct(product)) return false;

  return getVariants(product).some(variant => {
    const variantQuantity = toNumber(variant.bp_variant_quantity);
    const variantMinLevel = toNumber(variant.bp_variant_minimum_stock_level);
    return variantQuantity <= variantMinLevel;
  });
};

type PartialStatusLine = Pick<
  CartItem,
  | 'productID'
  | 'variantId'
  | 'Quantity'
  | 'mass'
  | 'bp_order_paid_quantity'
  | 'bp_order_paid_mass'
  | 'bp_order_completed'
  | 'price_type'
>;

/**
 * Parent variant cards are a group, not a SKU. Never return a quantity —
 * only whether any still-in-cart original variant line is unpaid.
 *
 * Reads solely from live cart items (same source-of-truth pattern as
 * non-variant cards). No cart row → no pill.
 */
export const getVariantPartialAttention = (
  cartItems: PartialStatusLine[] | undefined | null,
  productID: string,
  priceType?: string,
): 'unpaid' | 'paid' | null => {
  const liveLines = (cartItems ?? []).filter(
    item =>
      item.productID === productID &&
      item.bp_order_completed !== undefined,
  );
  if (!liveLines.length) return null;

  const hasUnpaid = liveLines.some(line => {
    const type = priceType || line.price_type;
    const unpaidMass =
      toNumber(line.mass) - toNumber(line.bp_order_paid_mass);
    const unpaidQuantity =
      toNumber(line.Quantity) - toNumber(line.bp_order_paid_quantity);
    return type === 'unit'
      ? unpaidMass > 0 && !isNumericEffectivelyZero(unpaidMass)
      : !isNumericEffectivelyZero(unpaidQuantity);
  });

  return hasUnpaid ? 'unpaid' : 'paid';
};
